import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { FileText, ShieldCheck, BookOpen, Loader2, Check, Search } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

/**
 * Attach-from-vault picker. Lets the investor select any number of:
 *   - Lease drafts (auto-generated current-state PDF per lease)
 *   - Custom uploaded lease PDFs (when the lease has one)
 *   - Signed e-signature lease PDFs
 *   - Year-end lease binders
 *
 * Returns selected refs to the parent via `onSelect(refs)` — refs are
 * resolved server-side at send time so the client never carries raw bytes.
 */
export default function AttachFromVaultDialog({ open, onClose, onSelect, currentSelection = [] }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("leases");
  const [selected, setSelected] = useState({}); // key: encoded ref → ref obj
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setQuery("");
    // Pre-populate from current selection so the dialog feels stateful
    const map = {};
    for (const ref of currentSelection || []) {
      map[refKey(ref)] = ref;
    }
    setSelected(map);
    api.get("/mail/attachable-items")
      .then((r) => setData(r.data))
      .catch((err) => {
        toast.error(formatApiError(err.response?.data?.detail) || "Could not load attachments");
        setData({ leases: [], signed: [], binders: [] });
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggle = (ref) => {
    const key = refKey(ref);
    setSelected((prev) => {
      const next = { ...prev };
      if (next[key]) delete next[key];
      else next[key] = ref;
      return next;
    });
  };

  const isSelected = (ref) => !!selected[refKey(ref)];

  const filteredLeases = useMemo(() => {
    if (!data) return [];
    const q = query.trim().toLowerCase();
    if (!q) return data.leases;
    return data.leases.filter((l) => `${l.label} ${l.sub}`.toLowerCase().includes(q));
  }, [data, query]);
  const filteredSigned = useMemo(() => {
    if (!data) return [];
    const q = query.trim().toLowerCase();
    if (!q) return data.signed;
    return data.signed.filter((l) => `${l.label} ${l.sub}`.toLowerCase().includes(q));
  }, [data, query]);
  const filteredBinders = useMemo(() => {
    if (!data) return [];
    const q = query.trim().toLowerCase();
    if (!q) return data.binders;
    return data.binders.filter((b) => `${b.label} ${b.sub}`.toLowerCase().includes(q));
  }, [data, query]);

  const submit = () => {
    onSelect(Object.values(selected));
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col" data-testid="attach-vault-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit" }}>Attach from your vault</DialogTitle>
        </DialogHeader>

        <div className="flex items-center gap-2 border-b border-slate-100 pb-2 mb-2 text-sm">
          <TabBtn active={tab === "leases"} onClick={() => setTab("leases")} icon={FileText} count={data?.leases?.length || 0} testid="attach-tab-leases">Lease drafts</TabBtn>
          <TabBtn active={tab === "signed"} onClick={() => setTab("signed")} icon={ShieldCheck} count={data?.signed?.length || 0} testid="attach-tab-signed">Signed</TabBtn>
          <TabBtn active={tab === "binders"} onClick={() => setTab("binders")} icon={BookOpen} count={data?.binders?.length || 0} testid="attach-tab-binders">Binders</TabBtn>
          <div className="ml-auto relative w-48">
            <Search className="w-3.5 h-3.5 absolute left-3 top-2.5 text-slate-400 pointer-events-none" />
            <input
              type="search"
              placeholder="Search…"
              className="input-clean w-full pl-8 text-xs"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="attach-vault-search"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="py-12 text-center text-sm text-slate-500" data-testid="attach-loading">
              <Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading vault…
            </div>
          ) : tab === "leases" ? (
            <List
              items={filteredLeases}
              renderRow={(l) => (
                <div key={`lease-${l.lease_id}`}>
                  <Row
                    primary={l.label}
                    secondary={`${l.sub} · Auto-generated draft`}
                    icon={FileText}
                    selected={isSelected({ kind: "lease_draft", lease_id: l.lease_id })}
                    onToggle={() => toggle({ kind: "lease_draft", lease_id: l.lease_id })}
                    testid={`attach-row-draft-${l.lease_id}`}
                  />
                  {l.has_custom_pdf ? (
                    <Row
                      primary={l.custom_pdf_filename || `Custom PDF · ${l.label}`}
                      secondary={`${l.sub} · Attorney-uploaded`}
                      icon={FileText}
                      accent="violet"
                      selected={isSelected({ kind: "lease_custom_pdf", lease_id: l.lease_id })}
                      onToggle={() => toggle({ kind: "lease_custom_pdf", lease_id: l.lease_id })}
                      testid={`attach-row-custom-${l.lease_id}`}
                    />
                  ) : null}
                </div>
              )}
              emptyText="No leases yet."
            />
          ) : tab === "signed" ? (
            <List
              items={filteredSigned}
              renderRow={(s) => (
                <Row
                  key={`signed-${s.envelope_id}`}
                  primary={s.label}
                  secondary={`${s.sub} · Signed ${s.completed_at ? new Date(s.completed_at).toLocaleDateString() : ""}`}
                  icon={ShieldCheck}
                  accent="emerald"
                  selected={isSelected({ kind: "lease_signed", envelope_id: s.envelope_id })}
                  onToggle={() => toggle({ kind: "lease_signed", envelope_id: s.envelope_id })}
                  testid={`attach-row-signed-${s.envelope_id}`}
                />
              )}
              emptyText="No fully-executed leases yet. Send a lease for e-signature first."
            />
          ) : (
            <List
              items={filteredBinders}
              renderRow={(b) => (
                <Row
                  key={`binder-${b.snapshot_id}`}
                  primary={b.label}
                  secondary={b.sub}
                  icon={BookOpen}
                  accent="sky"
                  selected={isSelected({ kind: "lease_binder", snapshot_id: b.snapshot_id })}
                  onToggle={() => toggle({ kind: "lease_binder", snapshot_id: b.snapshot_id })}
                  testid={`attach-row-binder-${b.snapshot_id}`}
                />
              )}
              emptyText="No year-end binders generated yet — visit Lease Vault → Year-end binders."
            />
          )}
        </div>

        <DialogFooter className="flex-shrink-0 pt-3 border-t border-slate-100">
          <div className="text-xs text-slate-500 mr-auto" data-testid="attach-selection-count">
            {Object.keys(selected).length} selected
          </div>
          <button type="button" onClick={onClose} className="btn-outline-dark" data-testid="attach-cancel">
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={Object.keys(selected).length === 0}
            className="btn-primary-dark inline-flex items-center gap-2"
            data-testid="attach-submit"
          >
            <Check className="w-4 h-4" /> Attach {Object.keys(selected).length || ""}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TabBtn({ active, onClick, icon: Icon, count, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-xs font-semibold px-3 py-1.5 rounded-sm border inline-flex items-center gap-1.5 ${active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}
      data-testid={testid}
    >
      <Icon className="w-3 h-3" /> {children}
      <span className={`text-[10px] px-1 rounded-sm ${active ? "bg-white/20" : "bg-slate-100"}`}>{count}</span>
    </button>
  );
}

function List({ items, renderRow, emptyText }) {
  if (!items || items.length === 0) {
    return <div className="py-10 text-center text-sm text-slate-500" data-testid="attach-list-empty">{emptyText}</div>;
  }
  return <div className="divide-y divide-slate-100" data-testid="attach-list">{items.map(renderRow)}</div>;
}

function Row({ primary, secondary, icon: Icon, accent = "slate", selected, onToggle, testid }) {
  const accentMap = {
    slate: "text-slate-500",
    emerald: "text-emerald-700",
    violet: "text-violet-700",
    sky: "text-sky-700",
  };
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`w-full text-left px-3 py-2.5 hover:bg-slate-50 flex items-center gap-3 ${selected ? "bg-slate-50" : ""}`}
      data-testid={testid}
    >
      <Icon className={`w-4 h-4 shrink-0 ${accentMap[accent]}`} />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-slate-900 truncate">{primary}</div>
        <div className="text-xs text-slate-500 truncate">{secondary}</div>
      </div>
      <div className={`w-5 h-5 rounded-sm border flex items-center justify-center shrink-0 ${selected ? "bg-slate-900 border-slate-900" : "bg-white border-slate-300"}`}>
        {selected ? <Check className="w-3 h-3 text-white" /> : null}
      </div>
    </button>
  );
}

function refKey(ref) {
  return `${ref.kind}|${ref.lease_id || ref.envelope_id || ref.snapshot_id || ""}`;
}
